import React from 'react'
import { createRoot } from 'react-dom/client'

// Styles
import './application.css'

// Stimulus
import { Application } from '@hotwired/stimulus'
import HelloController from '../controllers/hello_controller'
import SearchController from '../controllers/search_controller'
import DropdownController from '../controllers/dropdown_controller'
import PeriodToggleController from '../controllers/period_toggle_controller'
import PeriodFilterController from '../controllers/period_filter_controller'
import UserMenuController from '../controllers/user_menu_controller'
import SidebarCollapseController from '../controllers/sidebar_collapse_controller'
import CallDetailsController from '../controllers/call_details_controller'
import ModalController from '../controllers/modal_controller'

// React Components
import { TeamPerformanceDashboard } from '@/features/team-performance/TeamPerformanceDashboard'
import { TeamPerformanceDashboardV2 } from '@/features/team-performance/TeamPerformanceDashboardV2'
import DashboardOverview from '@/features/dashboard-overview/DashboardOverview'
import { LeadQualityDashboard } from '@/features/lead-quality/LeadQualityDashboard'
import { ELocalAnalyticsDashboard } from '@/features/elocal-analytics/ELocalAnalyticsDashboard'
import { AdWordsAnalyticsDashboard } from '@/features/adwords-analytics/AdWordsAnalyticsDashboard'
import { ColdProsAnalyticsDashboard } from '@/features/coldpros-analytics/ColdProsAnalyticsDashboard'

// Turbo
import '@hotwired/turbo-rails'

// Flowbite
import 'flowbite'

// Initialize Stimulus
const application = Application.start()
application.register('hello', HelloController)
application.register('search', SearchController)
application.register('dropdown', DropdownController)
application.register('period-toggle', PeriodToggleController)
application.register('period-filter', PeriodFilterController)
application.register('user-menu', UserMenuController)
application.register('sidebar-collapse', SidebarCollapseController)
application.register('call-details', CallDetailsController)
application.register('modal', ModalController)

// Store React roots to prevent double-mounting
const reactRoots = new Map<Element, ReturnType<typeof createRoot>>()

// Mount React components helper
function mountTeamPerformance() {
  const teamPerformanceRoot = document.getElementById('team-performance-root')
  if (teamPerformanceRoot && !reactRoots.has(teamPerformanceRoot)) {
    const props = JSON.parse(teamPerformanceRoot.dataset.props || '{}')
    const root = createRoot(teamPerformanceRoot)
    root.render(
      <React.StrictMode>
        <TeamPerformanceDashboard initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(teamPerformanceRoot, root)
  }
}

// Mount V2 React components helper
function mountTeamPerformanceV2() {
  const v2Root = document.getElementById('team-performance-v2-root')
  if (v2Root && !reactRoots.has(v2Root)) {
    const props = JSON.parse(v2Root.dataset.props || '{}')
    const root = createRoot(v2Root)
    root.render(
      <React.StrictMode>
        <TeamPerformanceDashboardV2 initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(v2Root, root)
  }
}

// Mount Dashboard Overview
function mountDashboardOverview() {
  const dashboardRoot = document.getElementById('dashboard-overview-root')
  if (dashboardRoot && !reactRoots.has(dashboardRoot)) {
    const props = JSON.parse(dashboardRoot.dataset.props || '{}')
    const root = createRoot(dashboardRoot)
    root.render(
      <React.StrictMode>
        <DashboardOverview initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(dashboardRoot, root)
  }
}

// Mount Lead Quality Dashboard
function mountLeadQualityDashboard() {
  const leadQualityRoot = document.getElementById('lead-quality-root')
  const dataScript = document.getElementById('lead-quality-data')
  if (leadQualityRoot && !reactRoots.has(leadQualityRoot)) {
    const props = dataScript ? JSON.parse(dataScript.textContent || '{}') : {}
    const root = createRoot(leadQualityRoot)
    root.render(
      <React.StrictMode>
        <LeadQualityDashboard initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(leadQualityRoot, root)
  }
}

// Mount eLocal Analytics Dashboard
function mountElocalAnalyticsDashboard() {
  const elocalRoot = document.getElementById('elocal-analytics-root')
  const dataScript = document.getElementById('elocal-analytics-data')
  if (elocalRoot && !reactRoots.has(elocalRoot)) {
    const props = dataScript ? JSON.parse(dataScript.textContent || '{}') : {}
    const root = createRoot(elocalRoot)
    root.render(
      <React.StrictMode>
        <ELocalAnalyticsDashboard initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(elocalRoot, root)
  }
}

// Mount AdWords Analytics Dashboard
function mountAdWordsAnalyticsDashboard() {
  const adwordsRoot = document.getElementById('adwords-analytics-root')
  const dataScript = document.getElementById('adwords-analytics-data')
  if (adwordsRoot && !reactRoots.has(adwordsRoot)) {
    const props = dataScript ? JSON.parse(dataScript.textContent || '{}') : {}
    const root = createRoot(adwordsRoot)
    root.render(
      <React.StrictMode>
        <AdWordsAnalyticsDashboard initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(adwordsRoot, root)
  }
}

// Mount ColdPros Analytics Dashboard
function mountColdProsAnalyticsDashboard() {
  const coldprosRoot = document.getElementById('coldpros-analytics-root')
  const dataScript = document.getElementById('coldpros-analytics-data')
  if (coldprosRoot && !reactRoots.has(coldprosRoot)) {
    const props = dataScript ? JSON.parse(dataScript.textContent || '{}') : {}
    const root = createRoot(coldprosRoot)
    root.render(
      <React.StrictMode>
        <ColdProsAnalyticsDashboard initialData={props} />
      </React.StrictMode>
    )
    reactRoots.set(coldprosRoot, root)
  }
}

// Mount all React components
function mountAllComponents() {
  mountTeamPerformance()
  mountTeamPerformanceV2()
  mountDashboardOverview()
  mountLeadQualityDashboard()
  mountElocalAnalyticsDashboard()
  mountAdWordsAnalyticsDashboard()
  mountColdProsAnalyticsDashboard()
}

// Mount on initial page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAllComponents)
} else {
  mountAllComponents()
}

// Turbo compatibility - remount React on navigation
document.addEventListener('turbo:load', mountAllComponents)
document.addEventListener('turbo:render', mountAllComponents)

// Clean up on Turbo navigation away
document.addEventListener('turbo:before-render', () => {
  reactRoots.forEach((root) => root.unmount())
  reactRoots.clear()
})
